import type { Action, ValidationResult } from "../../types/actions";
import type { Project } from "../../types/project";
import type { Track } from "../../types/timeline";
import type {
  MutableClip,
  MutableTimeline,
  MutableTrack,
} from "../../utils/immutable-updates";
import { createInverseAction, registerActionHandler } from "../registry";
import type { ActionHandler } from "../registry";
import { findClip } from "./clip-helpers";

interface TransformParams {
  readonly clipId?: unknown;
  readonly transform?: Record<string, unknown>;
}

function findTrackOfClip(project: Project, clipId: string): Track | null {
  for (const track of project.timeline.tracks) {
    if (track.clips.some((clip) => clip.id === clipId)) return track;
  }
  return null;
}

const transformUpdate: ActionHandler = {
  type: "transform/update",

  validate(action: Action, project: Project): ValidationResult {
    const params = action.params as TransformParams;
    const errors = [];

    if (typeof params.clipId !== "string" || !params.clipId) {
      return {
        valid: false,
        errors: [
          {
            code: "INVALID_PARAMS",
            message: "Clip ID is required and must be a string",
            path: "params.clipId",
          },
        ],
      };
    }

    if (!findClip(project, params.clipId)) {
      return {
        valid: false,
        errors: [
          {
            code: "CLIP_NOT_FOUND",
            message: `Clip with ID ${params.clipId} not found`,
            path: "params.clipId",
          },
        ],
      };
    }

    if (findTrackOfClip(project, params.clipId)?.locked) {
      errors.push({
        code: "TRACK_LOCKED",
        message: `Track containing clip ${params.clipId} is locked`,
        path: "params.clipId",
      });
    }

    if (!params.transform || typeof params.transform !== "object") {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Transform is required and must be an object",
        path: "params.transform",
      });
    }

    return { valid: errors.length === 0, errors };
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { clipId: string; transform: Record<string, unknown> };
    const timeline = project.timeline as MutableTimeline;

    timeline.tracks = timeline.tracks.map((track: MutableTrack) => ({
      ...track,
      clips: track.clips.map((clip: MutableClip) => {
        if (clip.id !== params.clipId) return clip;
        const prev = clip.transform;
        const next = params.transform as typeof prev;
        // Deep-merge the nested objects so a partial-axis update such as
        // { position: { x } } does not drop the other axis, matching the
        // store's own deep-merge behaviour.
        return {
          ...clip,
          transform: {
            ...prev,
            ...next,
            position: { ...prev.position, ...(next.position ?? {}) },
            scale: { ...prev.scale, ...(next.scale ?? {}) },
            anchor: { ...prev.anchor, ...(next.anchor ?? {}) },
          },
        };
      }),
    }));
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { clipId: string; transform?: Record<string, unknown> };
    const clip = findClip(projectBefore, params.clipId);
    if (!clip) return null;

    const before = clip.transform as unknown as Record<string, unknown>;
    // apply() spreads the inverse over the live transform, so a key the action
    // ADDED (fitMode on a clip that had none) has to come back as an explicit
    // undefined. Without it the spread has nothing to overwrite the added key
    // with, and the undo silently leaves it in place.
    const restored: Record<string, unknown> = { ...before };
    for (const key of Object.keys(params.transform ?? {})) {
      if (!(key in before)) restored[key] = undefined;
    }

    return createInverseAction(action, "transform/update", {
      clipId: params.clipId,
      transform: restored,
    });
  },
};

registerActionHandler(transformUpdate);
